import {filter, map} from 'lodash';
import {useCallback, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, InformationModal, ParticipantName} from 'components';
import {UserSessionContext} from 'context';
import {useUserSession} from 'hooks';
import {AnyParticipant, SessionGoal, SessionGoalType} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {sessionUtils} from 'utils';

import {GoalName} from '../GoalName';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE;

type Props = {
    open: boolean;
    onClose: () => void;
}

export const GoalsModal: React.FC<Props> = ({open, onClose}: Props) => {
    const {t} = useTranslation();
    const {goals, localParticipant, participants} = useUserSession();
    const {setGoalToConfirm, canMakeScreenshot} = useContext(UserSessionContext);

    const completedGoalsCount = useMemo(() => filter(goals, {completed: true}).length, [goals]);

    const renderRequestGoal = useCallback((goal: SessionGoal, participant: AnyParticipant) => {
        if (goal.completed ||
            goal.type === SessionGoalType.Screenshot && !canMakeScreenshot(goal.data.screenshotName) ||
            goal.type === SessionGoalType.AsyncRequest && !sessionUtils.canMakeAsyncRequest(participant, goal.data.asyncRequestType)) {
            return null;
        }

        return (
            <Icon
                name='plus-circle'
                button
                group
                size='sm'
                className='md:tw-hidden tw-ml-2'
                onClick={() => {
                    onClose();
                    setGoalToConfirm(goal);
                }}
            />);
    }, [canMakeScreenshot, setGoalToConfirm, onClose]);

    const renderParticipantGoals = useCallback((participant: AnyParticipant) => {
        if (!participant) {
            return null;
        }
        const participantGoals = filter(goals, {participantId: participant.id});
        if (!participantGoals.length) {
            return null;
        }
        return (
            <div key={participant.id} className='tw-my-2'>
                <ParticipantName participant={participant} localParticipant={localParticipant} />
                <div className='tw-ml-4'>
                    {map(participantGoals, (goal, index) => (
                        <div key={index} className='tw-flex tw-items-center tw-justify-between tw-my-1'>
                            <GoalName goal={goal} className='tw-mr-1' />
                            <div className='tw-flex-center'>
                                {goal.completed ?
                                    <Icon name='check-circle' color='success' size='sm' /> :
                                    <Icon name='slash' color='alert' size='sm' />}
                                {renderRequestGoal(goal, participant)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }, [goals, localParticipant, renderRequestGoal]);

    return (
        <InformationModal
            open={open}
            fullWidth
            maxWidth='sm'
            title={`${t(T_KEYS.LABELS.SESSION_GOALS)} (${completedGoalsCount} ${t(T_KEYS.LABELS.OF)} ${goals.length})`}
            onClose={onClose}
        >
            {renderParticipantGoals(localParticipant)}
            {map(participants, renderParticipantGoals)}
        </InformationModal>
    );
};